import Icon from 'components/atoms/Icon';
import PolicyDependentButton from 'components/molecules/PolicyDependentButtons/PolicyDependentButton';
import { jobRolesRoutes } from 'constants/navigation';
import {
  ProjectJobRoleFindManyDocument,
  ProjectJobRoleFindManyQuery,
  ProjectJobRoleFindManyQueryVariables,
  ProjectStatus,
  useProjectJobRoleDeleteOneMutation,
  useProjectPageGetProjectByIdQuery,
} from 'generated/graphql';
import { Link, Location, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  DropdownMenu,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './ProjectJobRolesOverviewPresentational.module.scss';
import { isRoleKickoffProject } from 'hooks/useProjectPolicyActions/useProjectPolicyActions.helpers';

type TProjectJobRoleButtonProps = {
  onClick?: () => void;
  projectStatus: ProjectStatus;
  isEnabledProjectAction: boolean;
};

const BUTTONS_CONTENT = {
  add: {
    label: 'Add',
    dataTestId: 'job-roles-overview__add-job-role-button',
  },
  addDropdown: {
    label: 'Add',
    dataTestId: 'job-roles-overview__add-job-role-dropdown-button',
  },
  create: {
    label: 'Create',
    dataTestId: 'job-roles-overview__create-job-role-button',
  },
  remove: {
    label: 'Remove',
    dataTestId: 'job-roles-overview__remove-job-role-button',
  },
  selectMainRole: {
    label: 'Set main role',
    dataTestId: 'job-roles-overview__select-main-role-button',
  },
};

export const AddProjectJobRoleButton = ({
  onClick,
  projectStatus,
  isEnabledProjectAction,
}: TProjectJobRoleButtonProps) => {
  const { label, dataTestId } = BUTTONS_CONTENT.add;
  return (
    <PolicyDependentButton
      id={dataTestId}
      disabled={!isEnabledProjectAction}
      customTooltipContent="Role Skills Profiles can't be added once stages have been added to the role."
      projectStatus={projectStatus}
      renderItem={(disabled) => (
        <Button
          size="medium"
          variant="secondary"
          onClick={onClick}
          data-testid={dataTestId}
          className={styles.buttonWithoutTooltip}
          disabled={disabled}
        >
          {label}
        </Button>
      )}
    />
  );
};

export const AddProjectJobRoleDropdownButton = ({
  projectId,
  projectStatus,
  isEnabledProjectAction,
  onDuplicateExistingRole,
}: {
  projectId: number;
  projectStatus: ProjectStatus;
  isEnabledProjectAction: boolean;
  onDuplicateExistingRole: () => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { label, dataTestId } = BUTTONS_CONTENT.addDropdown;

  const handleCreateNewRoleSkillsProfile = () => {
    navigate(jobRolesRoutes.createJobRole.url(), {
      state: { from: location, projectId },
    });
  };

  return (
    <PolicyDependentButton
      id={dataTestId}
      disabled={!isEnabledProjectAction}
      customTooltipContent="Role Skills Profiles can't be added once stages have been added to the role."
      projectStatus={projectStatus}
      renderItem={(disabled) =>
        disabled ? (
          <Button
            size="medium"
            variant="secondary"
            data-testid={dataTestId}
            className={styles.buttonWithoutTooltip}
            disabled
          >
            {label}
          </Button>
        ) : (
          <DropdownMenu
            label={label}
            showIcon={false}
            classNameDropdownMenu={styles.addRoleSkillsProfileDropdownMenu}
            renderButton={(_, __, handleTriggerDropdown) => (
              <Button
                variant="secondary"
                size="medium"
                onClick={handleTriggerDropdown}
                data-testid={dataTestId}
              >
                {label}
              </Button>
            )}
            options={[
              {
                label: 'Create new Role Skills Profile',
                onClick: handleCreateNewRoleSkillsProfile,
              },
              {
                label: 'Duplicate an existing Role Skills Profile',
                onClick: onDuplicateExistingRole,
              },
            ]}
          />
        )
      }
    />
  );
};

export const CreateProjectJobRoleButton = ({
  onClick,
  projectStatus,
  isEnabledProjectAction,
  linkState,
}: TProjectJobRoleButtonProps & {
  linkState: { from: Location; projectId: number };
}) => {
  const { label, dataTestId } = BUTTONS_CONTENT.create;
  return (
    <PolicyDependentButton
      id={dataTestId}
      disabled={!isEnabledProjectAction}
      customTooltipContent="Role Skills Profiles can't be created once stages have been added to the role."
      projectStatus={projectStatus}
      // Create button is a more special case because there is no good way to disable the link element, the user can still click the link and navigate to the create job role page even when the button is disabled.
      renderItem={(disabled) =>
        disabled ? (
          <Button
            size="medium"
            variant="primary"
            data-role="create-job-role"
            data-testid={dataTestId}
            className={styles.buttonWithoutTooltip}
            disabled={disabled}
          >
            {label}
          </Button>
        ) : (
          <Link to={jobRolesRoutes.createJobRole.url()} state={linkState}>
            <Button
              size="medium"
              variant="primary"
              data-role="create-job-role"
              data-testid={dataTestId}
              onClick={onClick}
              className={styles.buttonWithoutTooltip}
            >
              {label}
            </Button>
          </Link>
        )
      }
    />
  );
};

export const RemoveProjectJobRoleButton = ({
  id,
  projectId,
  name,
  isEnabledProjectAction,
  projectStatus,
}: {
  id: number;
  projectId: number;
  name: string;
  isEnabledProjectAction: boolean;
  projectStatus: ProjectStatus;
}) => {
  const { label, dataTestId } = BUTTONS_CONTENT.remove;
  const { handleMsgType } = useNotification();

  const { data: projectData } = useProjectPageGetProjectByIdQuery({
    variables: {
      id: projectId,
    },
  });

  const projectSource = projectData?.project?.source;

  const [deleteProjectJobRoleMutation, { loading }] =
    useProjectJobRoleDeleteOneMutation({
      variables: {
        id,
      },
      onCompleted: () =>
        handleMsgType({
          message: `Successfully deleted ${name} role`,
          type: TNotification.success,
        }),
      onError: (error) =>
        handleMsgType({
          type: TNotification.error,
          message:
            error?.message ||
            `Oops, some error happened while deleting ${name} role`,
        }),
      update: (cache, { data }) => {
        if (!data) return;

        const cachedProjectJobRoles = cache.readQuery<
          ProjectJobRoleFindManyQuery,
          ProjectJobRoleFindManyQueryVariables
        >({
          query: ProjectJobRoleFindManyDocument,
          variables: { id: projectId },
        });

        if (!cachedProjectJobRoles?.ProjectJobRoleFindMany?.length) return;

        const updatedProjectJobRoles =
          cachedProjectJobRoles?.ProjectJobRoleFindMany?.filter(
            (projectJobRole) => projectJobRole.id !== id,
          );

        cache.writeQuery({
          query: ProjectJobRoleFindManyDocument,
          variables: { id: projectId },
          data: {
            ProjectJobRoleFindMany: updatedProjectJobRoles,
          },
        });
      },
    });

  const handleDeleteProjectJobRole = () => {
    deleteProjectJobRoleMutation();
  };

  return (
    <PolicyDependentButton
      id={`${dataTestId}-${id}`}
      disabled={!isEnabledProjectAction}
      customTooltipContent={
        isRoleKickoffProject(projectSource, projectStatus)
          ? "Role Skills Profile can't be removed in a role created through the Role Wizard."
          : "Role Skills Profile can't be removed once stages have been added to the role."
      }
      projectStatus={projectStatus}
      renderItem={(disabled) => (
        <Button
          size="small"
          className={styles.buttonWithoutTooltip}
          variant="secondary"
          data-testid={dataTestId}
          onClick={handleDeleteProjectJobRole}
          loading={loading}
          disabled={disabled || loading}
          leftIcon={<Icon icon="delete_outline" className={styles.icon} />}
        >
          {label}
        </Button>
      )}
    />
  );
};

export const SetMainRoleButton = ({
  projectStatus,
  isEnabledProjectAction,
  handleSetMainRole,
  id,
  loading,
}: TProjectJobRoleButtonProps & {
  handleSetMainRole: () => void;
  id: number;
  loading: boolean;
}) => {
  const { label, dataTestId } = BUTTONS_CONTENT.selectMainRole;
  return (
    <PolicyDependentButton
      id={`${dataTestId}-${id}`}
      disabled={!isEnabledProjectAction}
      customTooltipContent="Main Role Skills Profile can't be changed once stages have been added to the role."
      projectStatus={projectStatus}
      renderItem={(disabled) => (
        <Button
          size="small"
          className={styles.btn}
          variant="tertiary"
          onClick={handleSetMainRole}
          loading={loading}
          disabled={disabled || loading}
        >
          {label}
        </Button>
      )}
    />
  );
};
